import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowRight, ChevronRight, Mail, Sparkles, Play,
  Building2, Wrench, HardHat, Activity,
  Cpu, Workflow, Users, X
} from 'lucide-react';
import VideoPlaceholder from '../../components/VideoPlaceholder';
import { introVideo } from '../../videos';
import PreviewBanner from './PreviewBanner';

const painPoints = [
  'Project data scattered across spreadsheets, emails, and shared drives',
  'No real-time visibility of portfolio health until issues escalate',
  'Inconsistent delivery processes between project managers and teams',
  'CDM and compliance documentation incomplete or missing',
  'Stakeholders disconnected from project progress',
  'Teams lacking confidence navigating PFI, lifecycle, or contractual obligations',
];

const pillars = [
  {
    icon: Cpu,
    eyebrow: 'Platform',
    title: 'Videra',
    desc: 'Our proprietary digital platform provides real-time visibility, mapped workflows, auditable processes, and a single source of truth across your entire delivery portfolio.',
  },
  {
    icon: Workflow,
    eyebrow: 'Process',
    title: 'Proven delivery methodology',
    desc: 'A structured, seven-stage delivery framework that standardises how projects are planned, governed, executed, and reported — reducing risk and eliminating inconsistency.',
  },
  {
    icon: Users,
    eyebrow: 'People',
    title: 'Training, coaching & culture change',
    desc: 'Technology only works when people adopt it. We deliver hands-on training, mentoring, and capability-building programmes that bring staff on the journey, embed new behaviours, and shift culture to support long-term performance.',
  },
];

const markets = ['PFI', 'NHS', 'FM Hard Services', 'Construction'];

const PreviewHome = () => (
  <div data-testid="preview-home">
    <PreviewBanner label="Home" livePath="/" liveLabel="Home" />

    {/* 1. HERO — unchanged from live */}
    <section className="relative pt-16 pb-16 md:pt-20 md:pb-20 overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-b from-slate-50 via-white to-white" />
      <div className="absolute inset-0 opacity-[0.04]" style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, #0f172a 1px, transparent 0)', backgroundSize: '24px 24px' }} />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-center">
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="lg:col-span-7">
            <div className="inline-flex items-center gap-2 bg-blue-50 border border-blue-100 text-blue-700 text-[11px] uppercase tracking-[0.2em] font-semibold px-3 py-1.5 rounded-full mb-6">
              <span className="w-1.5 h-1.5 rounded-full bg-blue-600 animate-pulse" /> Keystone Strategic Consultants · Makers of Videra
            </div>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl xl:text-[3.2rem] font-medium tracking-tight text-slate-900 leading-[1.08]">
              Delivery governance for <span className="text-blue-600">NHS, FM Hard Services</span> and <span className="text-blue-600">Construction.</span>
            </h1>
            <p className="mt-6 text-base lg:text-lg text-slate-600 leading-relaxed max-w-2xl">
              Keystone enables organisations to align their project delivery with the critical
              support functions that underpin performance. Through the Videra platform and
              targeted team training, we create a unified, controlled, and repeatable way of
              working across the business.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <Link to="/contact" className="inline-flex items-center justify-center bg-slate-900 text-white px-7 py-3.5 font-medium tracking-wide hover:bg-slate-800 transition-all group rounded-md">
                <Mail className="mr-2 w-4 h-4" /> Speak to our team
                <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Link>
              <a href="https://demo.videra.app/explore" className="inline-flex items-center justify-center bg-white border border-slate-300 text-slate-900 px-7 py-3.5 font-medium tracking-wide hover:border-slate-900 transition-all rounded-md group">
                Try the Videra demo <ChevronRight className="ml-1 w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </a>
            </div>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.15 }} className="lg:col-span-5">
            <div className="relative">
              <div className="bg-white border border-slate-200 rounded-2xl p-5 shadow-[0_1px_2px_rgba(15,23,42,0.04),0_8px_24px_-12px_rgba(15,23,42,0.12)]">
                <div className="flex items-center justify-between mb-4">
                  <p className="text-[11px] uppercase tracking-[0.2em] text-slate-500 font-semibold">Sector workspaces</p>
                  <Link to="/sectors" className="text-[11px] text-blue-600 hover:text-blue-700 font-medium inline-flex items-center group">Explore <ChevronRight className="w-3 h-3 ml-0.5 group-hover:translate-x-0.5 transition-transform" /></Link>
                </div>
                <div className="grid grid-cols-1 gap-2">
                  {[
                    { slug: 'nhs',           icon: Building2, label: 'NHS',              hint: 'Capital + lifecycle estates',  color: 'text-blue-600 bg-blue-50 border-blue-100' },
                    { slug: 'hard-services', icon: Wrench,    label: 'FM Hard Services', hint: 'PFI compliance · variations',     color: 'text-emerald-600 bg-emerald-50 border-emerald-100' },
                    { slug: 'construction',  icon: HardHat,   label: 'Construction',     hint: 'CDM · Building Safety Act 2022 · Golden Thread',    color: 'text-orange-600 bg-orange-50 border-orange-100' },
                  ].map((s) => (
                    <Link key={s.slug} to={`/sectors/${s.slug}`} className="group flex items-center gap-3 border border-slate-200 hover:border-slate-900 rounded-lg px-3 py-2.5 transition-colors bg-white">
                      <span className={`w-8 h-8 rounded-md border flex items-center justify-center ${s.color}`}><s.icon className="w-4 h-4" /></span>
                      <span className="flex-1 min-w-0">
                        <span className="block text-sm font-medium text-slate-900 leading-tight">{s.label}</span>
                        <span className="block text-[11px] text-slate-500 leading-tight mt-0.5 truncate">{s.hint}</span>
                      </span>
                      <ChevronRight className="w-4 h-4 text-slate-400 group-hover:text-slate-900 group-hover:translate-x-0.5 transition-all flex-shrink-0" />
                    </Link>
                  ))}
                </div>
              </div>
              <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.3 }} className="mt-4 sm:-ml-6 lg:-ml-10 sm:w-[calc(100%+1.5rem)] lg:w-[calc(100%+2.5rem)] bg-slate-900 text-white rounded-xl p-4 shadow-[0_20px_40px_-15px_rgba(15,23,42,0.45)] border border-slate-800">
                <div className="flex items-center gap-2 mb-3">
                  <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
                  <p className="text-[10px] uppercase tracking-[0.2em] text-slate-400 font-semibold">Live · portfolio status</p>
                </div>
                <div className="flex items-end justify-between">
                  <div>
                    <p className="text-2xl font-semibold tracking-tight">£64m</p>
                    <p className="text-[11px] text-slate-400 mt-0.5">Portfolio under governance</p>
                  </div>
                  <div className="flex items-center gap-1 text-emerald-400 text-xs font-medium">
                    <Activity className="w-3.5 h-3.5" /><span>Audit-ready</span>
                  </div>
                </div>
                <div className="mt-3 h-1.5 w-full bg-slate-800 rounded-full overflow-hidden"><div className="h-full bg-gradient-to-r from-blue-500 to-emerald-400" style={{ width: '78%' }} /></div>
                <div className="mt-2 flex items-center justify-between text-[10px] text-slate-500"><span>Stage-gate compliance</span><span className="text-slate-300 font-medium">78%</span></div>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>

    {/* 2. WHO IS KEYSTONE — NEW "Transform Your Project Delivery" copy */}
    <section className="py-20 md:py-28 bg-white border-y border-slate-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-start">
        <div className="lg:col-span-5">
          <p className="text-xs uppercase tracking-[0.2em] text-blue-600 font-semibold mb-4">Who we are</p>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-medium tracking-tight text-slate-900 leading-[1.1]">
            Transform your project delivery.
          </h2>
          <Link to="/about" className="inline-flex items-center mt-8 text-sm font-medium text-slate-900 hover:text-blue-600 transition-colors group">
            Read our story <ArrowRight className="ml-1 w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </Link>
        </div>
        <div className="lg:col-span-7 space-y-6 text-lg text-slate-600 leading-relaxed">
          <p>
            At <strong className="text-slate-900">Keystone Strategic Consultants</strong>, we help
            organisations solve complex operational, governance, and delivery challenges. Our work
            is powered by <strong className="text-slate-900">Videra</strong> — our proprietary
            digital platform designed to turn fragmented, inconsistent workflows into mapped,
            auditable, high-performance systems.
          </p>
          <p className="text-slate-900">
            We combine deep operational expertise with hands-on training, mentoring, and cultural
            alignment to ensure your teams adopt new ways of working with confidence. The result is
            transformation that doesn&apos;t just launch — it lasts.
          </p>

          {/* Markets strip */}
          <div className="pt-8 mt-2 border-t border-slate-200">
            <p className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold mb-3">Markets we serve</p>
            <div className="flex flex-wrap gap-2">
              {markets.map((m) => (
                <span key={m} className="inline-flex items-center text-sm font-medium text-slate-900 bg-slate-100 border border-slate-200 rounded-full px-3 py-1.5">
                  {m}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>

    {/* 2.5 SOUND FAMILIAR? — NEW */}
    <section className="py-20 md:py-28 bg-slate-50 border-b border-slate-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold mb-4">The problem we solve</p>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-medium tracking-tight text-slate-900 leading-tight">
            Sound familiar?
          </h2>
          <ul className="mt-8 space-y-4">
            {painPoints.map((p) => (
              <li key={p} className="flex items-start gap-3">
                <span className="w-6 h-6 rounded-md bg-red-50 border border-red-100 text-red-600 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <X className="w-3.5 h-3.5" />
                </span>
                <p className="text-base md:text-lg text-slate-700 leading-snug">{p}</p>
              </li>
            ))}
          </ul>
        </div>
        <div className="bg-slate-900 text-white rounded-2xl p-10 md:p-14 text-center">
          <p className="text-sm uppercase tracking-[0.2em] text-blue-400 font-semibold mb-6">Built to solve exactly this</p>
          <p className="text-2xl md:text-3xl lg:text-4xl font-medium tracking-tight leading-[1.15]">
            Keystone and <span className="text-blue-400">Videra</span> were built to solve exactly these problems.
          </p>
        </div>
      </div>
    </section>

    {/* 3. THE KEYSTONE + VIDERA APPROACH — Platform / Process / People */}
    <section className="py-20 md:py-28 bg-slate-900 text-white relative overflow-hidden">
      <div className="absolute inset-0 opacity-[0.04]" style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)', backgroundSize: '32px 32px' }} />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mb-14">
          <p className="text-xs uppercase tracking-[0.2em] text-blue-400 font-semibold mb-4">Our approach</p>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-medium tracking-tight leading-tight">
            The Keystone + Videra approach.
          </h2>
          <p className="mt-5 text-lg text-slate-300 leading-relaxed">
            We deliver transformation through three integrated pillars — Platform, Process, and People.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {pillars.map((p, i) => (
            <motion.div key={p.eyebrow} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08 }} className="bg-slate-800/60 border border-slate-700 rounded-xl p-7 hover:border-blue-500/40 transition-colors">
              <div className="w-11 h-11 rounded-lg bg-blue-500/15 border border-blue-500/30 text-blue-300 flex items-center justify-center mb-5">
                <p.icon className="w-5 h-5" />
              </div>
              <p className="text-xs uppercase tracking-[0.2em] text-blue-300 font-semibold mb-2">{p.eyebrow}</p>
              <h3 className="text-xl font-medium text-white mb-3">{p.title}</h3>
              <p className="text-sm text-slate-300 leading-relaxed">{p.desc}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>

    {/* 4. WHY WE BUILT IT — Practitioner narrative + intro video (unchanged) */}
    <section className="py-20 md:py-28 bg-slate-50 border-y border-slate-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mb-12">
          <p className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold mb-4">Why Videra exists</p>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-medium tracking-tight text-slate-900 leading-tight">
            Built by practitioners. <span className="text-slate-400">For practitioners.</span>
          </h2>
          <div className="mt-8 space-y-5 text-lg text-slate-600 leading-relaxed">
            <p>Videra wasn&apos;t dreamed up in a software lab. It was built from twenty years of running complex delivery — in hospitals, in PFI portfolios, on construction sites — and seeing the same governance failures, the same reporting bottlenecks, the same evidence gaps, time after time.</p>
            <p className="text-slate-900">Every feature in Videra exists because it solved a real problem on a real programme. Nothing speculative. Nothing for show.</p>
            <p className="text-sm text-slate-500 uppercase tracking-[0.18em] font-semibold pt-2">— Keystone Strategic Consultants</p>
          </div>
        </div>
        <div className="pt-10 border-t border-slate-200">
          <div className="max-w-2xl mb-8">
            <p className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold mb-3">Watch · 60 seconds</p>
            <h3 className="text-2xl md:text-3xl font-medium tracking-tight text-slate-900">Meet Keystone <span className="text-slate-400">& Videra.</span></h3>
          </div>
          <VideoPlaceholder number={1} videoUrl={introVideo.videoUrl} posterUrl={introVideo.posterUrl}
            title="Keystone Strategic Consultants — makers of Videra" lengthSec={60}
            scriptSummary="A one-minute introduction to who we are, the problems we solve, and how the Videra platform brings governance, evidence and visibility to complex delivery."
            timeline={['0:00–0:10 — Keystone introduction','0:10–0:25 — The delivery problem','0:25–0:45 — Videra in action','0:45–0:60 — Sectors & how to start']}
          />
        </div>
      </div>
    </section>

    {/* 5. READY TO TRANSFORM */}
    <section className="py-24 md:py-32 bg-slate-950 text-white relative overflow-hidden">
      <div className="absolute inset-0 opacity-[0.05]" style={{ backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)', backgroundSize: '32px 32px' }} />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-3xl mx-auto mb-14">
          <p className="text-xs uppercase tracking-[0.2em] text-blue-400 font-semibold mb-4">Next steps</p>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-medium tracking-tight">
            Ready to transform your delivery?
          </h2>
          <p className="mt-6 text-lg text-slate-400">
            Let&apos;s talk about how Keystone and Videra can elevate your operational performance.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 max-w-5xl mx-auto">
          <div className="bg-slate-900 border border-slate-800 rounded-2xl p-8 md:p-10 flex flex-col">
            <div className="inline-flex items-center gap-2 text-blue-300 text-[11px] uppercase tracking-[0.2em] font-semibold mb-5"><Mail className="w-3.5 h-3.5" /> Speak to our team</div>
            <h3 className="text-2xl font-medium text-white mb-3">Talk through your delivery challenge.</h3>
            <p className="text-sm text-slate-400 leading-relaxed mb-6 flex-grow">For organisations that want bespoke governance design and hands-on delivery support. Start with a 30-minute discovery call — no commitment.</p>
            <Link to="/contact" className="inline-flex items-center justify-center bg-white text-slate-900 px-6 py-3.5 rounded-md font-medium hover:bg-slate-100 transition-colors group">Speak to our team <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" /></Link>
          </div>
          <div className="bg-gradient-to-br from-blue-600 to-blue-700 rounded-2xl p-8 md:p-10 flex flex-col">
            <div className="inline-flex items-center gap-2 text-blue-100 text-[11px] uppercase tracking-[0.2em] font-semibold mb-5"><Sparkles className="w-3.5 h-3.5" /> Try Videra</div>
            <h3 className="text-2xl font-medium text-white mb-3">Feel the platform for yourself.</h3>
            <p className="text-sm text-blue-50 leading-relaxed mb-6 flex-grow">For teams that want to feel the platform first. Trial a sector workspace in 14 days. No card. No commitment.</p>
            <a href="https://demo.videra.app/explore" className="inline-flex items-center justify-center bg-white text-blue-700 px-6 py-3.5 rounded-md font-medium hover:bg-blue-50 transition-colors group"><Play className="mr-2 w-4 h-4" /> Try the demo <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" /></a>
          </div>
        </div>
      </div>
    </section>
  </div>
);

export default PreviewHome;
